import { LitElement, css, html } from 'lit'
import { CSSMixin, StorageMixin,  AppElement } from './mixins.js'


export class SplitView extends StorageMixin(CSSMixin(AppElement)) {
  static get storage() {
    return {
      split: {type: Number}
    }
  }

  static get properties() {
    return {
      vertical:   {type: Boolean},
      reversed:   {type: Boolean},
      minimum:    {type: Number},
      maximum:    {type: Number},
      noslide:    {type: Boolean}
    }
  }

  constructor() {
    super()
    this.vertical = false
    this.reversed = false
    this.closed   = false
    this.noslide  = false
    this.minimum  = 0
    this.maximum  = Number.MAX_SAFE_INTEGER
    this.moved    = false


    this.mo = new MutationObserver(() => {
      this.children[0].setAttribute("slot", "a")
      this.children[1].setAttribute("slot", "b")
    })

    this.mo.observe(this, { childList: true })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.mo && this.mo.disconnect()
  }


  firstUpdated() {
    let hs = this.getHostStyle()
    let tw = this.getHostVariable("--thumb-width") || "4px"
    let tt = this.reversed ? `${this.split}px ${tw} 1fr` : `1fr ${tw} ${this.split}px`
    hs[this.vertical ? "gridTemplateColumns" : "gridTemplateRows"] = tt

    this.children[0].setAttribute("slot", "a")
    this.children[1].setAttribute("slot", "b")
  }


  render() {
    return html`
        <slot name="a"></slot>
        <div id="slider" part="slider" @pointerdown="${this.lock}" @pointerup="${this.unlock}" @pointermove="${this.move}">
            <span id="button"></span>
        </div>
        <slot name="b"></slot>
    `
  }

  lock(evt) {
    this.moved = false
    evt.target.setPointerCapture(evt.pointerId)
  }

  unlock(evt) {
    this.releasePointerCapture(evt.pointerId)
    if (this.moved) {
      this.closed = false
    } else {
      this.switchState()
    } 
  }

  clamp(value) {
    return Math.max(this.minimum, Math.min(this.maximum, value))
  }

  move(evt) {
    if (evt.target.hasPointerCapture(evt.pointerId) && !this.noslide) {
      this.moved = true

      let tw = parseInt(this.getHostVariable("--thumb-width"))
        let bb = this.getBoundingClientRect()
        let hs = this.getHostStyle()
        hs.transition = `0s`

        if (this.vertical) {
            let pt = Math.min(bb.width - tw, Math.max(0, evt.x - bb.x)) 
            this.split = this.clamp(this.reversed ? pt : Math.round(bb.width - pt) - tw)
            hs.gridTemplateColumns =  this.reversed ? `${this.split}px ${tw}px 1fr` : `minmax(0, 1fr) ${tw}px ${this.split}px `
        } else {
            let pt = Math.min(bb.height - tw, Math.max(0, evt.y - bb.y)) 
            this.split = this.clamp(this.reversed ? pt : Math.round(bb.height - pt) - tw)
            hs.gridTemplateRows =  this.reversed ? `${this.split}px ${tw}px 1fr` : `minmax(0, 1fr) ${tw}px ${this.split}px `
        }
    }
  }


  switchState() {
    let hs = this.getHostStyle()
  
    let xa = `0px ${this.getHostVariable("--thumb-width") } 1fr`
    let xb = `1fr ${this.getHostVariable("--thumb-width") } 0px`
    
    let oa = `${this.split}px ${this.getHostVariable("--thumb-width") } 1fr`
    let ob = `1fr ${this.getHostVariable("--thumb-width")} ${this.split}px`

    let tt = this.closed ? (this.reversed ? oa : ob)  : (this.reversed ? xa : xb)
  
    hs.transition = `0.2s`
    hs[this.vertical ? "gridTemplateColumns" : "gridTemplateRows"] = tt
    if (this.noslide) {
      if (this.closed) {
        this.addEventListener("transitionend", evt => {
          for (let child of this.children) {
            child.style.overflow = "visible"
          }
        }, {once: true})
      } else {
        for (let child of this.children) {
          child.style.overflow = "hidden"
        }
      }
    }
    this.closed = !this.closed
  }

  close() {
    if (!this.closed) {
      this.switchState()
    }
  }

  open() {
    if (this.closed) {
      this.switchState()
    }
  }

  static get styles() {
    return css`
      :host {
        --thumb-width: 4px;
        border:solid black 1px;
        display: grid;
      }

      :host #slider {
        background: #0001;
        cursor: ns-resize;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      :host #button {
        height: 100%;
        width: 50px;
        background-image: linear-gradient(135deg, #555 25%, #ffffff 25%, #ffffff 50%, #555 50%, #555 75%, #ffffff 75%, #ffffff 100%);
        background-size: 5.66px 5.66px;
        cursor: pointer;
        border: solid #888 1px;
        opacity: 0.5;
      }

      :host([vertical]) {
        border: solid #0001 1px;
      }

      :host([vertical]) #slider {
        cursor: ew-resize;
      }

      :host([noslide]) #slider {
        cursor: default;
      }

      :host([vertical]) #button {
        width: 100%;
        height: 50px;
      }

      ::slotted(*) {
        overflow: auto;
        border: solid #0004 1px;
        min-height: 0px;
        min-width: 0px;
      }

      ::slotted(split-view) {
        border: none;
        overflow: hidden;
      }

      :host([thin]) {
        --thumb-width: 2px;
        border: none;
      }

      :host([thin]) #button {
        display: none;
      }

      :host([thin]) #slider {
        position: relative;
      }

      :host([thin]) #slider::after {
        content: "";
        display: block;
        background: #0000;
        width: calc(--thumb-width + 3px);
        top: 0px;
        left: 0px;
        position: absolute;
      }

      :host([thin][vertical]) #slider::after {
        height: 100%;
        width: calc(var(--thumb-width) + 3px);
      }

      :host([thin][horizontal]) #slider::after {
        width: 100%;
        height: calc(var(--thumb-width) + 3px);
      }

      :host([thin]) ::slotted(*) {
        border: none;
      }
    `
  }
}

window.customElements.define('split-view', SplitView)